import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  ENABLE_GUIDES_NAVIGATION,
  learningDestinations,
} from "@/navigation/learningNavigation";
import { puzzleDestinations } from "@/modules/puzzles/puzzleNavigation";
import { useEffect, useRef, useState } from "react";
import useTranslation from "next-translate/useTranslation";
import styles from "./Navigation.module.css";
import { FaAward, FaUser, FaChevronDown } from "react-icons/fa";
import { FaCog } from "react-icons/fa";
import { FaSignOutAlt } from "react-icons/fa";
import { FaBars } from "react-icons/fa";

import { useRouter } from "next/router";
import Link from "next/link";
// import dynamic from "next/dynamic";
import { FaTrophy } from "react-icons/fa";

import { signOut, useSignIn } from "../redux/signIn";
import { useUser } from "../redux/user";
import { useSiteData } from "../redux/siteData";

// const UserAvatar = dynamic(() => import("../modules/UserAvatar"));
// const GameInProgress = dynamic(() => import("./GameInProgress"));
// const MobileMenuDrawer = dynamic(() => import("./MobileMenuDrawer"));
const Notifications = dynamic(() => import("./Notifications"));
const FriendChat = dynamic(() => import("../modules/friendChat/FriendChat"));
// const FriendsNav = dynamic(() => import("./FriendsNav"));
// const ChallengesNav = dynamic(() => import("./ChallengesNav"));
// const ChallengesGuestNav = dynamic(() => import("./ChallengesGuestNav"));

import UserAvatar from "../modules/UserAvatar";
import GameInProgress from "./GameInProgress";
import MobileMenuDrawer from "./MobileMenuDrawer";
// import Notifications from "./Notifications";
// import FriendChat from "../modules/friendChat/FriendChat";
import OtherLoginNotice from "../modules/OtherLoginNotice";
import FriendsNav from "./FriendsNav";
import ChallengesNav from "./ChallengesNav";
import ChallengesGuestNav from "./ChallengesGuestNav";
import dynamic from "next/dynamic";
import { FREE_TIER } from "@chesshotel/constants";
import { useMembership } from "@/redux/membership";

const SHOW_MIGRATION_BANNER = false;

function NavLink({
  href,
  exact,
  activeClassName,
  children,
  prefetch = true,
  ...props
}) {
  const { pathname } = useRouter();
  const normalizedHref =
    typeof href === "string" && href.length > 1 && href.endsWith("/")
      ? href.slice(0, -1)
      : href;
  const isActive = exact
    ? pathname === normalizedHref
    : pathname === normalizedHref ||
      pathname.startsWith(`${normalizedHref}/`) ||
      (normalizedHref === "/puzzles" &&
        (pathname === "/puzzle" || pathname.startsWith("/puzzle/")));

  if (isActive && typeof activeClassName === "string") {
    props.className = [props.className, activeClassName]
      .filter(Boolean)
      .join(" ");
  }

  return (
    <Link {...props} href={href} prefetch={prefetch}>
      {children}
    </Link>
  );
}

function PuzzlePagesMenu({ label }: { label: string }) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const group = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverOpened = useRef(false);
  const router = useRouter();

  function cancelTimer() {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }
  function enter() {
    cancelTimer();
    if (open) return;
    timer.current = setTimeout(() => {
      hoverOpened.current = true;
      setOpen(true);
    }, 150);
  }
  function leave() {
    cancelTimer();
    if (hoverOpened.current)
      timer.current = setTimeout(() => setOpen(false), 180);
  }
  useEffect(() => {
    const close = () => {
      cancelTimer();
      hoverOpened.current = true;
      setOpen(false);
    };
    router.events.on("routeChangeStart", close);
    return () => {
      cancelTimer();
      router.events.off("routeChangeStart", close);
    };
  }, [router.events]);

  return (
    <div
      ref={group}
      className={`relative flex h-full items-center ${
        open ? "bg-slate-100" : ""
      }`}
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse") enter();
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse") leave();
      }}
    >
      <DropdownMenu
        modal={false}
        open={open}
        onOpenChange={(next) => {
          cancelTimer();
          hoverOpened.current = false;
          setOpen(next);
        }}
      >
        <Link
          href="/puzzles"
          className="!m-0 flex h-full items-center !pl-5 !pr-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        >
          <span
            className={
              router.pathname === "/puzzle" ||
              router.pathname.startsWith("/puzzle/") ||
              router.pathname === "/puzzles" ||
              router.pathname.startsWith("/puzzles/")
                ? styles.current
                : undefined
            }
          >
            {label}
          </span>
        </Link>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t("Puzzle pages")}
            className="flex h-full items-center pl-1 pr-5 text-base text-[#888] hover:text-slate-700 data-[state=open]:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
          >
            <FaChevronDown className="text-[10px]" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={0}
          className="min-w-[190px] !rounded-t-none rounded-b-md !border-0 !bg-slate-100 px-1 py-1.5 !shadow-md !animate-none data-[side=bottom]:slide-in-from-top-0"
          onPointerEnter={cancelTimer}
          onPointerLeave={(event) => {
            if (
              event.relatedTarget instanceof Node &&
              group.current?.contains(event.relatedTarget)
            ) {
              cancelTimer();
              return;
            }
            if (event.pointerType === "mouse") leave();
          }}
          onCloseAutoFocus={(event) => {
            if (hoverOpened.current) event.preventDefault();
          }}
          onKeyDown={() => {
            cancelTimer();
            hoverOpened.current = false;
          }}
        >
          {puzzleDestinations.map(({ href, menuLabel, icon: Icon }) => (
            <DropdownMenuItem
              key={href}
              asChild
              className="min-h-10 !px-4 py-2.5 gap-3 !text-slate-700 hover:!bg-slate-200 focus:!bg-slate-200"
            >
              <Link href={href}>
                <Icon
                  className="h-4 w-4 shrink-0 text-slate-500"
                  aria-hidden="true"
                />
                {t(menuLabel)}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function LearningPagesMenu({ label }: { label: string }) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const group = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverOpened = useRef(false);
  const router = useRouter();
  const isLearningPath = learningDestinations.some(
    ({ href }) =>
      router.pathname === href || router.pathname.startsWith(`${href}/`),
  );

  function cancelTimer() {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }

  function enter() {
    cancelTimer();
    if (open) return;
    timer.current = setTimeout(() => {
      hoverOpened.current = true;
      setOpen(true);
    }, 150);
  }

  function leave() {
    cancelTimer();
    if (hoverOpened.current) {
      timer.current = setTimeout(() => setOpen(false), 180);
    }
  }

  useEffect(() => {
    const close = () => {
      cancelTimer();
      hoverOpened.current = true;
      setOpen(false);
    };
    router.events.on("routeChangeStart", close);
    return () => {
      cancelTimer();
      router.events.off("routeChangeStart", close);
    };
  }, [router.events]);

  return (
    <div
      ref={group}
      className={`relative flex h-full items-center ${
        open ? "bg-slate-100" : ""
      }`}
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse") enter();
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse") leave();
      }}
    >
      <DropdownMenu
        modal={false}
        open={open}
        onOpenChange={(next) => {
          cancelTimer();
          hoverOpened.current = false;
          setOpen(next);
        }}
      >
        <Link
          href="/guides"
          locale={false}
          className="!m-0 flex h-full items-center !pl-5 !pr-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        >
          <span className={isLearningPath ? styles.current : undefined}>
            {label}
          </span>
        </Link>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={label}
            className="flex h-full items-center pl-1 pr-5 text-base text-[#888] hover:text-slate-700 data-[state=open]:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
          >
            <FaChevronDown className="text-[10px]" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={0}
          className="min-w-[210px] !rounded-t-none rounded-b-md !border-0 !bg-slate-100 px-1 py-1.5 !shadow-md !animate-none data-[side=bottom]:slide-in-from-top-0"
          onPointerEnter={cancelTimer}
          onPointerLeave={(event) => {
            if (
              event.relatedTarget instanceof Node &&
              group.current?.contains(event.relatedTarget)
            ) {
              cancelTimer();
              return;
            }
            if (event.pointerType === "mouse") leave();
          }}
          onCloseAutoFocus={(event) => {
            if (hoverOpened.current) event.preventDefault();
          }}
          onKeyDown={() => {
            cancelTimer();
            hoverOpened.current = false;
          }}
        >
          {learningDestinations.map(({ href, menuLabel, icon: Icon }) => (
            <DropdownMenuItem
              key={href}
              asChild
              className="min-h-10 gap-3 !px-4 py-2.5 !text-slate-700 hover:!bg-slate-200 focus:!bg-slate-200"
            >
              <Link href={href} locale={false}>
                <Icon
                  className="h-4 w-4 shrink-0 text-slate-500"
                  aria-hidden="true"
                />
                {t(menuLabel)}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function Navigation() {
  const { t } = useTranslation("common");

  const [userMenuActive, setUserMenuActive] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { username, memberLoggedIn } = useUser();
  const { loading } = useSignIn();

  const { loadingBarVisible, gamePath } = useSiteData();
  const { tier } = useMembership();

  const router = useRouter();

  const currentPath = router.pathname;
  const isShortNav = currentPath != "/"; //currentPath === gamePath || currentPath === "/analysis";
  const showMigrationBanner =
    SHOW_MIGRATION_BANNER && (currentPath === "/" || currentPath === "/play");

  function renderUserMenu() {
    return (
      <div className={styles.userMenu}>
        <Link
          onClick={() => setUserMenuActive(false)}
          className={styles.userMenuItem}
          href={"/profile"}
        >
          <div className={styles.icon}>
            <FaUser />
          </div>
          {t("Profile")}
        </Link>
        <Link
          onClick={() => setUserMenuActive(false)}
          className={styles.userMenuItem}
          href={"/leagues"}
        >
          <div className={styles.icon}>
            <FaTrophy />
          </div>
          {t("Leagues")}
        </Link>
        <Link
          onClick={() => setUserMenuActive(false)}
          className={styles.userMenuItem}
          href={"/settings/0"}
        >
          <div className={styles.icon}>
            <FaCog />
          </div>
          {t("Settings")}
        </Link>
        <button
          type="button"
          className={styles.userMenuItem}
          onClick={() => {
            setUserMenuActive(false);
            signOut();
          }}
        >
          <div className={styles.icon}>
            <FaSignOutAlt />
          </div>
          {t("Sign out")}
        </button>
      </div>
    );
  }

  function renderMemberMenu() {
    return (
      <>
        {userMenuActive && (
          <div
            onClick={() => setUserMenuActive(false)}
            className={styles.menuBg}
          ></div>
        )}
        <div className={"select-none md:top-0"}>
          {userMenuActive && renderUserMenu()}
          <div className="flex items-center space-x-3 md:space-x-4">
            <Notifications />

            <ChallengesNav />

            <FriendsNav />

            <button
              type="button"
              aria-label="Navigation menu"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation-drawer"
              onClick={() => setMobileMenuOpen((open) => !open)}
              className="flex bg-gray-200 rounded-full md:hidden active:opacity-60 hover:opacity-70"
            >
              <div className="flex -mr-1 cursor-pointer md:hidden">
                <div className="relative flex items-center justify-center text-gray-600 rounded-full w-9 h-9">
                  <FaBars />
                </div>
              </div>

              <div className="flex items-center space-x-2 cursor-pointer md:hover:opacity-70">
                <UserAvatar className="w-9 h-9" />
              </div>
            </button>

            <span className="hidden md:flex has-tooltip">
              <span className="tooltip">{username}</span>
              <div
                onClick={() => setUserMenuActive(true)}
                className="flex items-center space-x-2 cursor-pointer md:hover:opacity-70"
              >
                <UserAvatar className="w-9 h-9" />
              </div>
            </span>
          </div>
        </div>
      </>
    );
  }

  function renderGuestMenu() {
    return (
      <>
        <div className="flex items-center">
          <div className="hidden md:flex">
            <NavLink
              key="register"
              exact
              className={styles.register}
              activeClassName={styles.current}
              href={"/register"}
            >
              {t("Sign up")}
            </NavLink>
          </div>
          <NavLink
            key="signIn"
            exact
            className={styles.signIn}
            activeClassName={styles.currentSignIn}
            href={"/signIn"}
          >
            {t("Log in")}
          </NavLink>

          <ChallengesGuestNav />

          <button
            type="button"
            aria-label="Navigation menu"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation-drawer"
            onClick={() => setMobileMenuOpen((open) => !open)}
            className="flex ml-3 cursor-pointer md:hidden"
          >
            <div className="relative flex items-center justify-center text-gray-600 bg-gray-200 rounded-full w-9 h-9 hover:bg-gray-300">
              <FaBars />
            </div>
          </button>
        </div>

        {/* <div className={"ml-auto md:hidden"}>
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="btn btn-icon btn-icon-lg btn-goast"
            >
              <FaBars />
            </button>
          </div> */}
      </>
    );
  }

  const isGame = currentPath === gamePath;

  return (
    <>
      <MobileMenuDrawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />

      <div
        className={`sticky md:static z-30 top-0 flex items-center border-b border-b-grey-100 bg-white 
      pl-7 lg:pl-12 pr-5 lg:pr-12
      ${isGame ? "hidden md:flex" : ""}`}
      >
        {
          <div
            className={
              loadingBarVisible
                ? styles.loading + " " + styles.loadingActive
                : styles.loading
            }
          />
        }

        <div className="flex items-center space-x-2 lg:mr-12 md:mr-2">
          <Link href={memberLoggedIn ? "/play" : "/"}>
            <div className={isShortNav ? "crown" : "crown md:logo"} />
          </Link>
          {/* {!memberLoggedIn && (
            <div className={"ml-auto md:hidden"}>
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="btn btn-icon btn-icon-lg btn-goast"
              >
                <FaBars />
              </button>
            </div>
          )} */}
        </div>
        <div
          className={`bg-white
        ${
          isShortNav
            ? "flex top-16 justify-between w-full h-[58px]"
            : "justify-between w-full md:flex h-[58px] md:h-[100px]"
        }`}
        >
          <div className={styles.menu}>
            <NavLink
              exact={false}
              prefetch={false}
              activeClassName={styles.current}
              href="/play"
            >
              {t("Play")}
            </NavLink>
            <NavLink exact activeClassName={styles.current} href="/players">
              {t("Players")}
            </NavLink>
            <PuzzlePagesMenu label={t("Puzzles")} />
            {ENABLE_GUIDES_NAVIGATION ? (
              <LearningPagesMenu label={t("Learn")} />
            ) : (
              <NavLink
                exact
                activeClassName={styles.current}
                href="/learn/openings"
              >
                {t("Learn")}
              </NavLink>
            )}

            {memberLoggedIn && tier === FREE_TIER && (
              <NavLink
                exact
                activeClassName={{}}
                className="text-orange-400"
                href="/memberships"
              >
                <span className="flex items-center gap-2 text-orange-400">
                  <FaAward className="text-[15px]" />
                  {t("Premium")}
                </span>
              </NavLink>
            )}
          </div>
        </div>

        {!loading && (
          <div className={`${styles.authMenu}`}>
            {memberLoggedIn ? renderMemberMenu() : renderGuestMenu()}
          </div>
        )}

        <FriendChat />
      </div>
      <OtherLoginNotice />
      {showMigrationBanner && (
        <div className={styles.migrationBanner} role="status">
          Server migration work is in progress today. The site is available as
          usual.
        </div>
      )}
      <GameInProgress />
    </>
  );
}
